import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useDebounce } from '@core/hooks/useDebounce';
import ApiResponse from '@core/types/apiResponse';

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface SearchPage<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
  per_page?: number;
  from?: number;
}

export interface SearchParams {
  freetext: string;
  page: number;
  perPage: number;
  filters: Record<string, any>;
}

/**
 * Contract for a search fetch function.
 * Wrap your API call in useCallback so the reference stays stable:
 *
 *   const fetchFn = useCallback<SearchFetchFn<User>>(
 *     (params) => searchUsers(scopeId, params),
 *     [scopeId],
 *   );
 */
export type SearchFetchFn<T> = (
  params: SearchParams,
) => Promise<ApiResponse<SearchPage<T>>>;

export interface UseSearchOptions<T> {
  /** Memoised fetch function. A new reference re-triggers the search. */
  fetchFn: SearchFetchFn<T>;
  /** Debounce delay for the freetext query in ms. Defaults to 300. */
  debounceMs?: number;
  /** Records per page. Defaults to 20. */
  initialPerPage?: number;
  /** Pause all fetching until true (useful when a scope ID isn't ready yet). */
  enabled?: boolean;
  /**
   * When true, syncs query / filters / page with URL search params using
   * replace-history so it doesn't pollute the browser's back-stack.
   *
   * - Initialises state from the URL on mount.
   * - Updates the URL whenever state changes.
   *
   * Use on dedicated search pages. Leave false for embedded table components
   * that live inside a larger page (KpiTable, ActivityTable, etc.).
   */
  syncUrl?: boolean;
}

export interface UseSearchReturn<T> {
  data: T[];
  total: number;
  page: number;
  lastPage: number;
  from: number | undefined;
  loading: boolean;
  error: string | null;
  /** Raw (un-debounced) query — bind to SearchBar's onQueryChange. */
  query: string;
  /**
   * Update the freetext query. Page resets to 1; the actual fetch fires after
   * the debounce delay. Filters remain active alongside the query.
   */
  setQuery: (q: string) => void;
  /** Currently applied filters (after the user clicks "Apply Filters"). */
  filters: Record<string, any>;
  /** Replace all filters and reset to page 1. Freetext remains active. */
  setFilters: (f: Record<string, any>) => void;
  setPage: (p: number) => void;
  reload: () => void;
  reset: () => void;
  perPage: number;
  activeFilterCount: number;
  /**
   * Always false — freetext and filters are sent together.
   * Kept for interface compatibility with existing SearchBar usages.
   */
  filtersIgnored: boolean;
  /**
   * Current freetext value for seeding/syncing the search input UI.
   * Pass as `initialQuery` to <SearchBar />.
   */
  initialQuery: string;
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useSearch<T>(options: UseSearchOptions<T>): UseSearchReturn<T> {
  const {
    fetchFn,
    debounceMs = 300,
    initialPerPage = 20,
    enabled = true,
    syncUrl = false,
  } = options;

  // useSearchParams must be called unconditionally (it's a hook).
  // It is only used when syncUrl is true.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // ─── Latest-ref pattern keeps doFetch stable ───────────────────────────
  const fetchFnRef = useRef(fetchFn);
  useEffect(() => { fetchFnRef.current = fetchFn; });

  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; });

  // ─── Lazy-initialise state from URL when syncUrl is true ───────────────
  const [query, setQueryState] = useState<string>(() => {
    if (!syncUrl) return '';
    return searchParams.get('q') ?? '';
  });

  const [page, setPageState] = useState<number>(() => {
    if (!syncUrl) return 1;
    return Number(searchParams.get('page') || '1');
  });

  const [filters, setFiltersState] = useState<Record<string, any>>(() => {
    if (!syncUrl) return {};
    const result: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key !== 'q' && key !== 'page') result[key] = value;
    });
    return result;
  });

  // ─── Response state ────────────────────────────────────────────────────
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [responsePage, setResponsePage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [from, setFrom] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounce(query, debounceMs);

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && v !== null && v !== '',
  ).length;

  // Freetext and filters are sent together — no mutual exclusion.
  const filtersIgnored = false;

  // ─── URL sync ──────────────────────────────────────────────────────────
  // Track which filter keys were previously written so we can delete stale ones.
  const prevFilterKeysRef = useRef<Set<string>>(new Set());
  // Prevent re-triggering the effect from our own setSearchParams call.
  const lastSyncedRef = useRef('');
  // Skip the very first render (state already read from URL on mount).
  const isMountRef = useRef(true);
  const previousPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (!syncUrl) return;
    if (previousPathnameRef.current === location.pathname) return;

    previousPathnameRef.current = location.pathname;

    const nextQuery = searchParams.get('q') ?? '';
    const nextPage = Number(searchParams.get('page') || '1');
    const nextFilters: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key !== 'q' && key !== 'page') nextFilters[key] = value;
    });

    prevFilterKeysRef.current = new Set(Object.keys(nextFilters));
    lastSyncedRef.current = '';
    isMountRef.current = true;

    setQueryState(nextQuery);
    setPageState(nextPage);
    setFiltersState(nextFilters);
  }, [syncUrl, location.pathname, searchParams]);

  useEffect(() => {
    if (!syncUrl) return;

    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }

    const nextParams: Record<string, string> = {};
    if (debouncedQuery) nextParams.q = debouncedQuery;
    if (page > 1) nextParams.page = String(page);

    const nextFilterKeys = new Set<string>();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        nextParams[k] = String(v);
        nextFilterKeys.add(k);
      }
    });

    const serialized = JSON.stringify(nextParams);
    if (serialized === lastSyncedRef.current) return;
    lastSyncedRef.current = serialized;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Remove previously managed params
      next.delete('q');
      next.delete('page');
      prevFilterKeysRef.current.forEach((k) => next.delete(k));
      // Write current params
      Object.entries(nextParams).forEach(([k, v]) => next.set(k, v));
      return next;
    }, { replace: true });

    prevFilterKeysRef.current = nextFilterKeys;
  }, [syncUrl, debouncedQuery, filters, page, setSearchParams]);

  // ─── Core fetch ────────────────────────────────────────────────────────
  const doFetch = useCallback(async (p: number, q: string, f: Record<string, any>) => {
    if (!enabledRef.current) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchFnRef.current({
        freetext: q,
        page: p,
        perPage: initialPerPage,
        filters: f,
      });

      if (response.isSuccess()) {
        const pageData = response.getData();
        setData(pageData?.data ?? []);
        setTotal(pageData?.total ?? 0);
        setResponsePage(pageData?.current_page ?? p);
        setLastPage(pageData?.last_page ?? 1);
        setFrom(pageData?.from);
      } else {
        const err = response.getError();
        setError(err?.detail ?? 'An error occurred while fetching data.');
        setData([]);
      }
    } catch {
      setError('An unexpected error occurred.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [initialPerPage]);

  useEffect(() => {
    doFetch(page, debouncedQuery, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedQuery, filters, doFetch]);

  // ─── Public API ────────────────────────────────────────────────────────

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
    setPageState(1);
  }, []);

  const setFilters = useCallback((f: Record<string, any>) => {
    setFiltersState(f);
    setPageState(1);
  }, []);

  const setPage = useCallback((p: number) => setPageState(p), []);

  const reload = useCallback(() => {
    doFetch(page, debouncedQuery, filters);
  }, [doFetch, page, debouncedQuery, filters]);

  const reset = useCallback(() => {
    setQueryState('');
    setFiltersState({});
    setPageState(1);
  }, []);

  return {
    data,
    total,
    page: responsePage,
    lastPage,
    from,
    loading,
    error,
    query,
    setQuery,
    filters,
    setFilters,
    setPage,
    reload,
    reset,
    perPage: initialPerPage,
    activeFilterCount,
    filtersIgnored,
    initialQuery: query,
  };
}
